import { classNames, iconProps, useGlobalId } from '@zambezi/sdk/common'
import PropTypes from 'prop-types'
import React, {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import ComboBox from './ComboBox'
import { inputGhostRenderer } from './renderers'
import TagLookupList from './TagLookupList'

/**
 * Create a Tag Lookup component.
 *
 * @template T
 * @template Item
 * @type {React.ForwardRefRenderFunction<HTMLElement, import('./interfaces').TagLookupProps<T, Item>>}
 * @param {import('./interfaces').TagLookupProps<T, Item>} param0
 * @param {import('react').Ref<import('./interfaces').LookupRef>} ref
 */
function tagLookup (
  {
    id,
    tags,
    className,
    inputProps,
    onChange,
    editable,
    removeIconProps,
    value: propsValue,
    renderInput,
    tagsShown,
    'aria-label': ariaLabel,
    ...props
  },
  ref
) {
  const lookupId = useGlobalId('zb-tag-lookup-')
  const inputId = id || lookupId
  const tagsId = useGlobalId('zb-tag-lookup-tags-')
  const [tagData, setTagData] = useState(tags)
  const [value, setValue] = useState(propsValue || '')
  const tagEntered = useRef(0)
  const {
    disabledItemsIndices: _disabledItemsIndices,
    suggestions,
    getSuggestionValue,
    disabled
  } = props
  const disabledItemsIndices = useMemo(() => {
    const indices = _disabledItemsIndices.slice()
    for (let i = 0; i < suggestions.length; i++) {
      const value = getSuggestionValue(suggestions[i])
      if (tagData.indexOf(value) !== -1) {
        indices.push(i)
      }
    }
    return indices
  }, [tagData, _disabledItemsIndices, suggestions, getSuggestionValue])
  const addTagData = useCallback(
    (event, value) => {
      if (value) {
        setTagData((tagData) => {
          const tags =
            tagData.indexOf(value) === -1 ? tagData.concat(value) : tagData
          onChange(event, { tags, method: 'add' })

          return tags
        })
        setValue('')
      }
    },
    [onChange]
  )
  const removeTagData = useCallback(
    (suggestion, event) =>
      setTagData((tagData) => {
        const tags = tagData.filter((tag) => tag !== suggestion)
        onChange(event, { tags, method: 'remove' })
        return tags
      }),
    [onChange]
  )
  const handleChange = useCallback((event, { value, method }) => {
    if (method === 'click' || method === 'enter') {
      addTagData(event, value)
      if (method === 'enter') tagEntered.current = Date.now()
    } else setValue(value)
  }, [])
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        // Only add tags if they were not added from change already.
        if (tagEntered.current + 200 < Date.now()) {
          addTagData(event, event.target.value)
        }
      }
      inputProps.onKeyDown && inputProps.onKeyDown(event)
    },
    [inputProps.onKeyDown]
  )
  const getOnTagRemove = useCallback(
    (tag) => (editable || tags.indexOf(tag) === -1 ? removeTagData : null),
    [tags, editable, removeTagData]
  )

  const renderTagsInput = useCallback(
    (props) => {
      return (
        <Fragment>
          <TagLookupList
            disabled={disabled}
            getOnTagRemove={getOnTagRemove}
            inputId={inputId}
            removeIconProps={removeIconProps}
            tags={tagData}
            tagsId={tagsId}
            tagsShown={tagsShown}
          />
          {renderInput(props)}
        </Fragment>
      )
    },
    [
      disabled,
      inputId,
      tagData,
      tagsShown,
      tagsId,
      removeIconProps,
      getOnTagRemove,
      renderInput
    ]
  )

  useEffect(() => setTagData(tags), [tags])
  useEffect(() => setValue(propsValue), [propsValue || ''])
  return (
    <div
      className={classNames('zb-tag-lookup', className)}
      aria-describedby={tagData.length ? tagsId : null}
      aria-label={ariaLabel}
    >
      <ComboBox
        {...props}
        id={inputId}
        disabledItemsIndices={disabledItemsIndices}
        value={value}
        onChange={handleChange}
        inputProps={{ ...inputProps, onKeyDown: handleKeyDown }}
        ref={ref}
        renderInput={renderTagsInput}
      />
    </div>
  )
}

/**
 * @template T
 * @template Item
 * @type {React.ForwardRefExoticComponent<import('./interfaces').TagLookupProps<T, Item> & React.RefAttributes<import('./interfaces').LookupRef>>}
 */
const TagLookup = forwardRef(tagLookup)

TagLookup.displayName = 'TagLookup'

TagLookup.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.string),
  editable: PropTypes.bool,
  suggestions: PropTypes.array,
  getSuggestionValue: PropTypes.func,
  defaultValue: PropTypes.string,
  renderInput: PropTypes.func,
  suggestionMatches: PropTypes.func,
  renderSuggestion: PropTypes.func,
  onSuggestionSelected: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  withGrouping: PropTypes.bool,
  renderGroupLabel: PropTypes.func,
  getGroupChildren: PropTypes.func,
  onGroupLabelClick: PropTypes.func,
  selectedIconProps: iconProps,
  removeIconProps: iconProps,
  iconProps,
  inputProps: PropTypes.object,
  disabledItemsIndices: PropTypes.arrayOf(PropTypes.number),
  tagsShown: PropTypes.number
}

TagLookup.defaultProps = {
  tags: [],
  suggestions: [],
  disabledItemsIndices: [],
  onChange: () => {},
  getSuggestionValue: String,
  renderInput: inputGhostRenderer,
  inputProps: {},
  editable: true,
  renderNoResultsMessage: () => `No matches found. Press Enter to add this.`,
  tagsShown: Number.POSITIVE_INFINITY
}

export default TagLookup
